import { ArrowUpRight, Check, Heart } from 'lucide-react';
import {
  CV_SUMMARY_MAX_LENGTH,
  CandidateProfileTip,
  CvColor,
  CvTemplate,
  JobApplicationStatus,
  SAVE_INTENT_PARAM,
} from '@hr-outsourcing/shared';
import { type BadgeProps } from '@/components/ui';
import { JOBS_SITE_URL, PEOPLY_SITE_URL, PUBLIC_JOBS_HOME_URL } from '@/constants/jobsSite';
import { PUBLIC_JOB_LISTINGS_PATH } from '@/constants/jobListing';

export const CAREERS_PATH = '/careers';

export const CAREERS_ROUTES = {
  dashboard: `${CAREERS_PATH}/dashboard`,
  cv: `${CAREERS_PATH}/cv`,
  profile: `${CAREERS_PATH}/profile`,
  recommended: `${CAREERS_PATH}/recommended`,
  saved: `${CAREERS_PATH}/saved`,
  applications: `${CAREERS_PATH}/applications`,
  interviews: `${CAREERS_PATH}/interviews`,
  job: `${CAREERS_PATH}/jobs/:slug`,
  company: `${CAREERS_PATH}/companies/:slug`,
} as const;

export const PUBLIC_COMPANY_ROUTE = `${PUBLIC_JOB_LISTINGS_PATH}/company/:slug`;
export const PUBLIC_JOB_ROUTE = `${PUBLIC_JOB_LISTINGS_PATH}/:slug`;
// The listings' own domain has no /jobs prefix: a listing at /<slug>, a company at /company/<slug>
// (mirrors the server's JOBS_SITE_COMPANY_PAGE_PATH).
export const JOBS_SITE_COMPANY_ROUTE = '/company/:slug';
export const JOBS_SITE_JOB_ROUTE = '/:slug';
// Public listing and company pages: on the listings' own domain when it has one (absolute links,
// no /jobs prefix), under /jobs otherwise.
const PUBLIC_LISTINGS_BASE = JOBS_SITE_URL || PUBLIC_JOB_LISTINGS_PATH;
export const publicCompanyPath = (slug: string) => `${PUBLIC_LISTINGS_BASE}/company/${encodeURIComponent(slug)}`;
export const careersCompanyPath = (slug: string) => `${CAREERS_PATH}/companies/${encodeURIComponent(slug)}`;
export const careersJobPath = (slug: string) => `${CAREERS_PATH}/jobs/${encodeURIComponent(slug)}`;
// A guest's "save" goes through sign-in; this flag on the listing page saves it once they are back.
export const careersJobSavePath = (slug: string) => `${careersJobPath(slug)}?${SAVE_INTENT_PARAM}=1`;
export const publicJobPath = (slug: string) => `${PUBLIC_LISTINGS_BASE}/${encodeURIComponent(slug)}`;
export const HR_SESSION_STORAGE_KEY = 'auth-storage';
// The public header's "About": the listings' own domain's About page when it has one (no link to
// Peoply), Peoply's "About us" otherwise.
export const PUBLIC_ABOUT_PATH = JOBS_SITE_URL ? `${JOBS_SITE_URL}/about` : '/about';
export const PUBLIC_ABOUT_LABEL_KEY = JOBS_SITE_URL ? 'publicPages.layout.navAbout' : 'jobDetail.guest.navAbout';
// Peoply's home: the way back for someone signed in to the HR app.
export const PEOPLY_HOME_PATH = `${PEOPLY_SITE_URL}/`;
export const JOBS_SEARCH_PARAMS = {
  q: 'q',
  city: 'city',
  workType: 'workType',
  seniority: 'seniority',
  country: 'country',
  salary: 'salary',
  minSalary: 'minSalary',
} as const;

export const JOBS_FILTER_SEPARATOR = ',';
export const JOBS_MIN_SALARY_OPTIONS = [25_000, 50_000, 75_000, 100_000, 150_000] as const;
export const JOBS_SALARY_BAND_STARTS: number[] = [0, ...JOBS_MIN_SALARY_OPTIONS];
export const JOBS_PAGE_PARAM = 'page';
export const JOBS_SORT_PARAM = 'sort';
export const JOBS_SORTS = { newest: 'newest', match: 'match' } as const;
export const CAREERS_LANGUAGE_OPTIONS = [
  { value: 'tr', code: 'TR', label: 'Türkçe' },
  { value: 'en', code: 'EN', label: 'English' },
  { value: 'de', code: 'DE', label: 'Deutsch' },
  { value: 'el', code: 'EL', label: 'Ελληνικά' },
  { value: 'fr', code: 'FR', label: 'Français' },
] as const;

export const JOB_FIT_CARD_TONES = { ok: 'success', miss: 'danger', unknown: 'default' } as const;
export const JOB_FIT_BADGE_VARIANTS = { ok: 'emerald', miss: 'red', unknown: 'muted' } as const satisfies Record<
  string,
  NonNullable<BadgeProps['variant']>
>;

export const APPLICATION_TABS = { all: 'all', active: 'active', interview: 'interview', closed: 'closed' } as const;
export const APPLICATION_STEPS = [
  JobApplicationStatus.NEW,
  JobApplicationStatus.REVIEWING,
  JobApplicationStatus.INTERVIEW,
  JobApplicationStatus.OFFER,
  JobApplicationStatus.HIRED,
] as const;
export const APPLICATION_ENDED_STATUSES: readonly JobApplicationStatus[] = [
  JobApplicationStatus.REJECTED,
  JobApplicationStatus.WITHDRAWN,
];

export const APPLICATION_STATUS_TONES = {
  [JobApplicationStatus.NEW]: 'violet',
  [JobApplicationStatus.REVIEWING]: 'emerald',
  [JobApplicationStatus.INTERVIEW]: 'amber',
  [JobApplicationStatus.OFFER]: 'orange',
  [JobApplicationStatus.HIRED]: 'success',
  [JobApplicationStatus.REJECTED]: 'muted',
  [JobApplicationStatus.WITHDRAWN]: 'muted',
} as const satisfies Record<JobApplicationStatus, NonNullable<BadgeProps['variant']>>;

export const JOBS_RAIL_PREVIEW_COUNT = 2;
// Interviews on the dashboard (today's and the soonest); every interview is on the interviews page.
export const DASHBOARD_INTERVIEW_COUNT = 2;

export const SAVED_JOBS_FILTERS = { all: 'all', closingSoon: 'closingSoon', remote: 'remote' } as const;
export const SAVED_JOBS_SORTS = { savedAt: 'savedAt', publishedAt: 'publishedAt' } as const;
export const SAVED_JOB_CLOSING_SOON_DAYS = 7;
export const JOBS_SIDE_PANEL_MEDIA_QUERY = '(min-width: 1280px)';
export const COMPANY_LOGO_TONES = ['rose', 'emerald', 'violet', 'orange', 'sky'] as const;
export const NEXT_PARAM = 'next';

export const CAREERS_QUERY_KEYS = {
  profile: ['careers', 'profile'],
  account: ['careers', 'account'],
  strength: ['careers', 'profile-strength'],
  recommendations: ['careers', 'recommendations'],
  recommendationsWithApplied: (countries: string[] = []) =>
    ['careers', 'recommendations', 'with-applied', countries] as const,
  saved: ['careers', 'saved-jobs'],
  applications: ['careers', 'applications'],
  invitations: ['careers', 'invitations'],
  oauthProviders: ['careers', 'oauth-providers'],
  job: (slug: string) => ['careers', 'job', slug],
  jobs: (search: object) => ['careers', 'jobs', search],
  publicJob: (slug: string) => ['public-job', slug],
  publicCompany: (slug: string) => ['public-company', slug],
  publicJobs: (search: object) => ['public-jobs', search],
  ownListing: (slug: string) => ['own-listing', slug],
} as const;

export const JOB_APPLICATION_COVER_LETTER_MAX_LENGTH = 3000;

export const READINESS_TIP_COUNT = 3;

// Validation errors of the careers API: the field names' wordings, and the message naming no field.
export const CAREERS_VALIDATION_FIELDS_PREFIX = 'careers.validationFields';
export const CAREERS_INVALID_INPUT_MESSAGE = 'MESSAGES.CANDIDATE.INVALID_INPUT';

export const LINKEDIN_PROFILE_URL_PATTERN = /^https:\/\/([a-z]{2,3}\.)?linkedin\.com\/in\/[^/?#\s]+\/?$/i;

export const JOBS_PATH = PUBLIC_JOBS_HOME_URL;
export const JOBS_HOW_IT_WORKS_ID = 'nasil-calisir';
export const JOBS_HOW_IT_WORKS_PATH = `${JOBS_PATH}#${JOBS_HOW_IT_WORKS_ID}`;
export const JOBS_GUEST_STEPS = ['explore', 'profile', 'apply'] as const;
export const JOBS_GUEST_BENEFITS = [
  { key: 'save', icon: Heart },
  { key: 'oneProfile', icon: Check },
  { key: 'match', icon: ArrowUpRight },
] as const;
export const JOBS_GUEST_PROFILE_EXAMPLE = 35;
export const JOBS_GUEST_CITY_COUNT = 8;
export const PUBLIC_JOBS_SSR_ATTR = 'data-ssr-jobs';
export const CANDIDATE_TOKEN_STORAGE_KEY = 'peoply-careers-session';
export const CANDIDATE_CV_MAX_BYTES = 5 * 1024 * 1024;
export const CANDIDATE_PHOTO_MAX_BYTES = 2 * 1024 * 1024;
export const CANDIDATE_PHOTO_MIME_TYPES = ['image/jpeg', 'image/png'] as const;
export const CV_TEMPLATES = [
  CvTemplate.CLASSIC,
  CvTemplate.MODERN,
  CvTemplate.MINIMAL,
  CvTemplate.CORPORATE,
  CvTemplate.EXECUTIVE,
  CvTemplate.TIMELINE,
  CvTemplate.CREATIVE,
] as const;
export const CV_COLORS: { value: CvColor; swatch: string }[] = [
  { value: CvColor.BLUE, swatch: '#1d4ed8' },
  { value: CvColor.TEAL, swatch: '#0d9488' },
  { value: CvColor.GREEN, swatch: '#15803d' },
  { value: CvColor.PURPLE, swatch: '#7c3aed' },
  { value: CvColor.CRIMSON, swatch: '#be123c' },
  { value: CvColor.ORANGE, swatch: '#c2410c' },
  { value: CvColor.SLATE, swatch: '#334155' },
];
export const CV_TEMPLATE_SWATCHES: Record<CvTemplate, string> = {
  [CvTemplate.CLASSIC]: '#1d4ed8',
  [CvTemplate.MODERN]: '#0d9488',
  [CvTemplate.MINIMAL]: '#171717',
  [CvTemplate.CORPORATE]: '#0f2a4a',
  [CvTemplate.EXECUTIVE]: '#92400e',
  [CvTemplate.TIMELINE]: '#4f46e5',
  [CvTemplate.CREATIVE]: '#db2777',
};

export const CV_STEP_PARAM = 'step';

export const CV_STEP_BY_TIP: Record<CandidateProfileTip, number> = {
  [CandidateProfileTip.ADD_CV]: 0,
  [CandidateProfileTip.ADD_HEADLINE]: 0,
  [CandidateProfileTip.ADD_SUMMARY]: 0,
  [CandidateProfileTip.EXPAND_SUMMARY]: 0,
  [CandidateProfileTip.ADD_CITY]: 0,
  [CandidateProfileTip.ADD_EXPERIENCE]: 1,
  [CandidateProfileTip.DESCRIBE_EXPERIENCE]: 1,
  [CandidateProfileTip.ADD_EDUCATION]: 2,
  [CandidateProfileTip.ADD_SKILLS]: 3,
  [CandidateProfileTip.ADD_LANGUAGES]: 3,
  [CandidateProfileTip.ADD_DESIRED_POSITIONS]: 4,
  [CandidateProfileTip.ADD_WORK_TYPES]: 4,
};

export const cvFirstMissingStepPath = (tips: readonly CandidateProfileTip[]): string => {
  const step = tips.length ? Math.min(...tips.map((tip) => CV_STEP_BY_TIP[tip])) : 0;
  return `${CAREERS_ROUTES.cv}?${new URLSearchParams({ [CV_STEP_PARAM]: String(step) })}`;
};

export const CANDIDATE_PROFILE_LIMITS = {
  headline: 150,
  summary: CV_SUMMARY_MAX_LENGTH,
  city: 80,
  country: 80,
  experience: { company: 150, position: 150, startDate: 20, endDate: 20, description: 2000 },
  education: { institution: 200, degree: 120, field: 150, graduationYear: 10 },
  language: { name: 60, level: 40 },
} as const;

export const CV_PICKER_YEAR_ONLY = 'year-only';
export const CV_PICKER_ONGOING = 'ongoing';
export const CV_TEMPLATES_PANEL = 'cv-templates';
export const CV_PREVIEW_DEBOUNCE_MS = 700;
export const CV_PREVIEW_ZOOM = { min: 0.5, max: 1.5, step: 0.25, initial: 0.75, fit: 1 } as const;
export const BRAND_NAME_LANG = 'en';
